import {createAsyncThunk} from '@reduxjs/toolkit'
import {getAnalyticsContext} from 'modules/analytics/selectors'
import {bookOfferCreationConfirm} from 'modules/book/slice'
import {getUserCountry} from 'modules/meta/selectors'
import Router from 'next/router'
import {setFormData as setSessionStorageFormData} from 'services/sessionStorage'
import {RootState} from 'store'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'
import {getMerchantOfRecordCode} from 'utils/getMerchantOfRecordCode'
import getNewLocationUrl from 'utils/getNewLocationUrl'
import {urlParams} from 'utils/urlParams'
import {CONFIRMATION_PAGE_PATH} from 'utils/urls'

// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {getDevicePlatform} from '@vio/core/src/_web/utils/userAgent/device'
import {BookingStatus} from '@vio/core/src/api-types/bovio/response/booking_status'
import {getIsAuthenticated, getUser} from '@vio/core/src/auth/modules/selectors'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {BookingCreationResponse} from '@vio/core/src/booking/types/BookingCreationResponse'

import {trackBookingSuccess} from '../../analytics/paymentFormTracking'
import {getUnifiedOfferCheck} from '../../offerCheck/selectors'
import {getHotelData} from '../../searchApi/selectors'
import {getRedirectContext} from '../selectors'
import {confirm} from '../slice'

export interface HandleBookingConfirmationArg {
  bookingData: BookingStatus
  isWithThreeDomainSecure: boolean
}

export const handleBookingConfirmation = createAsyncThunk<
  void,
  HandleBookingConfirmationArg,
  {state: RootState}
>(
  'bookingCreation/handleBookingConfirmation',
  async (arg, {dispatch, getState}) => {
    const state = getState()
    const {
      checkIn,
      checkOut,
      hotelId,
      paymentMethodType: urlPaymentMethodType
    } = urlParams()
    const {bookingData, isWithThreeDomainSecure} = arg
    const {customer: bookingContactDetails = null, paymentMethodType} =
      getRedirectContext(state) || {}
    const {offerContext, priceContext} = getAnalyticsContext(state)
    const brand = getBrand(state)
    const user = getUser(state)
    const hotel = getHotelData(state)
    const isAuthenticated = getIsAuthenticated(state)
    const offerData = getUnifiedOfferCheck(state).data
    const {id: bookingId} = bookingData
    const {booking, merchantOfRecord, providerCode} = offerData || {}
    const merchantOfRecordCode = getMerchantOfRecordCode(
      merchantOfRecord,
      providerCode
    )
    trackBookingSuccess({
      brand,
      bookingRequestId: getCurrentBookingRequestId(),
      merchantOfRecordCode,
      offerContext,
      priceContext,
      user,
      bookingContactDetails,
      hotel,
      dispatch,
      isAuthenticated: Boolean(isAuthenticated),
      userCountry: getUserCountry(state),
      platform: getDevicePlatform(),
      // On Resume flow, fall back to the paymentMethodType in url
      paymentMethod: paymentMethodType ?? urlPaymentMethodType
    })

    setSessionStorageFormData()

    // Adding timeout to show success message before redirect to confirmation page after successful booking
    setTimeout(() => {
      dispatch(bookingRetrieveApi.util.resetApiState())

      const newUrl = getNewLocationUrl({
        url: CONFIRMATION_PAGE_PATH,
        search: {
          bookingId,
          checkIn: checkIn || booking?.checkIn,
          checkOut: checkOut || booking?.checkOut,
          hotelId: hotelId || booking?.hotelId,
          resumeFlow: undefined,
          processing: undefined
        }
      })

      Router.push(newUrl)
    }, 2500)

    dispatch(confirm())
    dispatch(
      bookOfferCreationConfirm({
        ...(bookingData as Partial<BookingCreationResponse>),
        isWithThreeDomainSecure
      })
    )
  }
)
